/**
 * What a TA action board can DO, grouped by tab — the denominator the per-board panel needs.
 *
 * ── Why a list, when the counts arrive on their own ──────────────────────────────────────────
 * The usage data says what a recruiter did. The useful question is the opposite one: what has
 * nobody shown them yet? That cannot be derived from events, because a feature used zero times
 * emits nothing at all — an absence is invisible unless something else says it should have been
 * there. This file is that something else, and it is the whole reason the panel can say "Lola has
 * never chased an interviewer" rather than only "Lola did 40 things".
 *
 * Same contract as `featureCatalog.ts`: hand-kept, and it degrades honestly. A feature missing
 * here is a feature the panel says nothing about, rather than one it says something false about.
 * `boardFeatures.test.ts` fails if an event named here has no description, so a typo cannot
 * quietly become a feature nobody can ever have used.
 */
import { describeEvent } from './eventDescriptions';

export interface BoardFeature {
  /** What a recruiter would call it — the coaching conversation's own words. */
  label: string;
  /** Any of these firing counts as having used it. */
  events: string[];
}

export interface BoardTabFeatures {
  /** Tab slug as the URL and the section rollup spell it. */
  tab: string;
  label: string;
  features: BoardFeature[];
}

export const BOARD_TABS: BoardTabFeatures[] = [
  {
    tab: 'feedback',
    label: 'Feedback',
    features: [
      { label: 'Reject and archive in Lever', events: ['ta_board_archive'] },
      { label: 'Advance a candidate a stage', events: ['ta_board_move', 'ta_stage_changed'] },
      { label: 'Add the AGG paragraph to a rejection', events: ['ta_agg_feedback_toggled'] },
      { label: 'Write a note on a candidate', events: ['candidate_note_added'] },
    ],
  },
  {
    tab: 'scheduling',
    label: 'Scheduling',
    features: [
      { label: 'Hold slots in the calendar', events: ['ta_board_holds', 'ta_board_holds_released'] },
      { label: 'Withdraw a slot offer', events: ['ta_board_withdraw'] },
      { label: 'Ask the panel on Slack', events: ['ta_board_ask'] },
      { label: 'Save an interview panel', events: ['ta_board_panel_saved'] },
      { label: 'Edit an interview link from the board', events: ['ta_board_edit_panel'] },
      {
        label: 'Invite a whole first round at once',
        events: ['ta_first_invite_review_opened', 'ta_first_invite_confirmed'],
      },
      { label: 'Book a final-round chain', events: ['ta_board_fr_book', 'ta_board_fr_config'] },
    ],
  },
  {
    tab: 'sla',
    label: 'SLA',
    features: [
      { label: 'Chase an interviewer for feedback', events: ['ta_board_feedback_nudged'] },
      { label: 'Park a candidate with a wake-up date', events: ['sla_snooze_set'] },
      { label: 'Wake a parked candidate early', events: ['sla_snooze_released'] },
    ],
  },
  {
    tab: 'requests',
    label: 'Candidate requests',
    features: [
      { label: 'Send a reply to a candidate', events: ['ta_requests_reply_sent'] },
      { label: 'Rewrite a draft with ✨', events: ['ta_requests_reply_refined'] },
      { label: 'Rate a draft 👍 / 👎', events: ['ta_requests_draft_rated'] },
      { label: 'Schedule a reply for later', events: ['ta_reply_scheduled'] },
      { label: 'Propose interview slots inside a reply', events: ['ta_requests_slots_proposed'] },
      {
        label: 'Let a candidate reschedule themselves',
        events: ['ta_requests_reschedule_link_inserted'],
      },
      { label: 'Reschedule for a candidate', events: ['ta_requests_reschedule_opened'] },
      { label: 'Cancel an interview on request', events: ['ta_requests_interview_cancelled'] },
      { label: 'Clear a handled request', events: ['ta_requests_marked_read'] },
      { label: 'Set a follow-up reminder', events: ['ta_reminder_created'] },
    ],
  },
];

/** Every event any catalogued feature can be proved by. Exported for the coverage test. */
export const BOARD_FEATURE_EVENTS = BOARD_TABS.flatMap((tab) =>
  tab.features.flatMap((feature) => feature.events)
);

export interface FeatureUse {
  label: string;
  /** Total across every event that proves this feature, in the whole range. */
  count: number;
}

export interface TabUse {
  tab: string;
  label: string;
  used: FeatureUse[];
  /** Catalogued features with no event at all — what a coaching conversation is for. */
  unused: string[];
}

/**
 * Split the catalogue against what one board actually recorded.
 *
 * Counted across EVERY tab rather than within the catalogued one. A feature reached from two tabs
 * — a reminder set from Scheduling, a reply sent from a request card opened on Feedback — would
 * otherwise be reported as never used by someone who uses it daily, and "you have never tried
 * this" is exactly the claim that must not be wrong.
 */
export function summariseBoardFeatures(byTabEvent: Record<string, Record<string, number>>): TabUse[] {
  const counts = new Map<string, number>();
  for (const events of Object.values(byTabEvent ?? {})) {
    for (const [name, count] of Object.entries(events ?? {})) {
      counts.set(name, (counts.get(name) ?? 0) + (Number(count) || 0));
    }
  }

  return BOARD_TABS.map((tab) => {
    const used: FeatureUse[] = [];
    const unused: string[] = [];
    for (const feature of tab.features) {
      const total = feature.events.reduce((sum, event) => sum + (counts.get(event) ?? 0), 0);
      if (total > 0) used.push({ label: feature.label, count: total });
      else unused.push(feature.label);
    }
    used.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    return { tab: tab.tab, label: tab.label, used, unused };
  });
}

/** The one-line explanation for a feature's first event, for the hover on a checklist row. */
export function describeFeature(label: string): string | null {
  for (const tab of BOARD_TABS) {
    const feature = tab.features.find((entry) => entry.label === label);
    if (feature) return describeEvent(feature.events[0]);
  }
  return null;
}

export interface FeatureAdoption {
  label: string;
  /** Times it was used across every board in the range. */
  uses: number;
  /** How many boards used it at all — the number that says whether it has landed. */
  boards: number;
}

export interface TabAdoption {
  tab: string;
  label: string;
  features: FeatureAdoption[];
}

/**
 * How far each board feature has spread across the team — least-adopted first.
 *
 * ── Why REACH and not volume ────────────────────────────────────────────────────────────────
 * A feature used 400 times looks like a success and can still be one person's habit. The question
 * behind "which features are underrated" is how many recruiters have found it, so the list is
 * ordered by the number of BOARDS that used it, with volume only breaking the tie. That ordering
 * is the finding: everything at the top is something the team has not picked up.
 *
 * Aggregate on purpose. The per-board panel beside this answers "who", and it is scoped and
 * justified there; this one is about the feature, and naming people in it would add nothing.
 */
export function adoptionAcrossBoards(rows: { byTabEvent: Record<string, Record<string, number>> }[]): TabAdoption[] {
  const perFeature = new Map<string, { uses: number; boards: Set<number> }>();

  rows.forEach((row, index) => {
    const counts = new Map<string, number>();
    for (const events of Object.values(row.byTabEvent ?? {})) {
      for (const [name, count] of Object.entries(events ?? {})) {
        counts.set(name, (counts.get(name) ?? 0) + (Number(count) || 0));
      }
    }
    for (const tab of BOARD_TABS) {
      for (const feature of tab.features) {
        const uses = feature.events.reduce((sum, event) => sum + (counts.get(event) ?? 0), 0);
        if (uses <= 0) continue;
        const entry = perFeature.get(feature.label) ?? { uses: 0, boards: new Set<number>() };
        entry.uses += uses;
        entry.boards.add(index);
        perFeature.set(feature.label, entry);
      }
    }
  });

  return BOARD_TABS.map((tab) => ({
    tab: tab.tab,
    label: tab.label,
    features: tab.features
      .map((feature) => {
        const entry = perFeature.get(feature.label);
        return { label: feature.label, uses: entry?.uses ?? 0, boards: entry?.boards.size ?? 0 };
      })
      .sort((a, b) => a.boards - b.boards || a.uses - b.uses || a.label.localeCompare(b.label)),
  }));
}

/**
 * `ta_board_holds` → "Hold slots in the calendar", where the catalogue knows the event.
 *
 * The feature list is written in the words a recruiter would use, and those are the words this
 * board should be read in too — `ta_board_ask` is precise and unreadable. Null for anything the
 * catalogue does not claim, so the caller can fall back to the identifier rather than to a
 * prettified guess at what it means.
 */
const LABEL_BY_EVENT = new Map(
  BOARD_TABS.flatMap((tab) => tab.features.flatMap((f) => f.events.map((e) => [e, f.label])))
);

export function featureLabelFor(event: string): string | null {
  return LABEL_BY_EVENT.get(event) ?? null;
}
